import { Recording, getEventRecordings, recordings } from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import { transformServerRecordings } from './utils';

import {
  VideoAsset,
  getEventVideoLibraryRecordings,
  getRoomRecordings
} from '@/studio/videoLibrary/videoLibraryService';
import { isVLReadyEvent } from '@/utils';

async function fetchEventVideoLibraryRecordings(
  eventId: string
): Promise<{ recordings: VideoAsset[]; totalCount: number }> {
  try {
    const recordingsData = await getEventVideoLibraryRecordings({ event_id: eventId });
    return { recordings: recordingsData.assets, totalCount: recordingsData.totalCount };
  } catch (error) {
    logger.error('Error fetching event video library recordings', error);
    return { recordings: [], totalCount: 0 };
  }
}

async function fetchRoomRecordings(eventId: string): Promise<Recording[]> {
  try {
    const roomRecordings = await getRoomRecordings({ event_id: eventId });
    return roomRecordings.assets;
  } catch (error) {
    logger.error('Error fetching room recordings', error);
    return [];
  }
}

const actions = {
  async loadRecordings(
    { dispatch, commit, state },
    { eventId, isVideoLibrary, eventStartTime }: { eventId: string; isVideoLibrary: boolean; eventStartTime?: string }
  ) {
    const { searchTerm, currentPage, recordingsPerPage } = state;

    commit('setIsLoading', true);

    // In the EU environment (public-pr53), use the video library path only for events
    // started after June 15 2026. For older EU events fall back to the legacy path.
    const useVideoLibrary = isVideoLibrary && isVLReadyEvent(eventStartTime);

    try {
      if (useVideoLibrary) {
        const results = await Promise.allSettled([
          fetchEventVideoLibraryRecordings(eventId),
          fetchRoomRecordings(eventId)
        ]);

        const eventRecordings = results[0].status === 'fulfilled' ? results[0].value.recordings : [];
        const totalCount = results[0].status === 'fulfilled' ? results[0].value.totalCount : 0;
        const roomRecordings = results[1].status === 'fulfilled' ? results[1].value : [];

        commit('setRecordings', [...eventRecordings, ...roomRecordings]);
        commit('setRecordingsCount', totalCount);
      } else {
        const recordingsData = await getEventRecordings({
          id: eventId,
          queryParams: {
            offset: (currentPage - 1) * recordingsPerPage,
            limit: recordingsPerPage,
            title: searchTerm
          }
        });
        const eventRecordings = recordingsData.results || [];
        commit('setRecordings', transformServerRecordings(eventRecordings));
        commit('setRecordingsCount', recordingsData.count);
      }
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        { msg: 'Error Loading Recordings, Please Try Again', mode: 'error' },
        { root: true }
      );
    } finally {
      commit('setIsLoading', false);
    }
  },

  async getSessionTranscripts(_, broadcastId: string) {
    return recordings({ id: broadcastId, queryParams: {} });
  }
};

export default actions;
